import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { getMovieById, getSpecialById, specials } from "@/lib/content";
import type { Movie } from "@/types/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SpecialPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const topicParam = searchParams.get("topic") ?? "";
  const selected = getSpecialById(topicParam) ?? specials[0];
  const topicMovies = selected.movieIds
    .map((id) => getMovieById(id))
    .filter((movie): movie is Movie => Boolean(movie));
  const relatedSpecials = selected.relatedIds
    .map((id) => getSpecialById(id))
    .filter((item): item is (typeof specials)[number] => Boolean(item));

  usePageMeta({
    title: `${selected.title} - 专题策划 - “奸情”影视剧推荐`,
    description: `${selected.intro.slice(0, 70)}…… 本期专题收录 ${topicMovies.length} 部影视剧，含片单、推荐理由与相关专题。`,
    keywords: `${selected.title},影视专题,片单推荐,剧情解析,奸情影视`,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-8 pt-24 md:px-8 md:pb-10 md:pt-32 lg:px-12">
          <div className="w-full">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "专题策划" }, { label: selected.title }]} />
            <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <h1 className="font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
                  专题策划
                </h1>
                <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-[15px]">
                  围绕一个主题把剧集重新排列，是理解创作者意图最快的方式。每期专题包含片单、推荐理由与延伸阅读。
                </p>
              </div>
              <p className="text-xs text-foreground-500">
                共 <span className="font-label font-bold text-primary-800">{specials.length}</span> 个专题
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {specials.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSearchParams({ topic: item.id }, { replace: true })}
                  className={`rounded-full px-3.5 py-2 text-xs font-medium transition-colors whitespace-nowrap md:text-sm ${
                    item.id === selected.id
                      ? "bg-primary-900 text-background-50"
                      : "bg-background-50 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
                  }`}
                  aria-pressed={item.id === selected.id}
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-8 md:px-8 md:py-12 lg:px-12">
          <div className="relative w-full overflow-hidden rounded-lg">
            <div className="relative h-[320px] w-full md:h-[420px]">
              <img
                src={selected.cover}
                alt={`${selected.title}专题主图`}
                title={`${selected.title} - 影视专题策划`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/20" />
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-9">
                <p className="font-label text-[11px] font-bold uppercase tracking-[0.28em] text-accent-300">
                  {selected.kicker}
                </p>
                <h2 className="mt-2 font-heading text-2xl font-black text-background-50 md:text-4xl">
                  {selected.title}
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-background-100 md:text-[15px]">
                  {selected.intro}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-background-200">
                  <span className="rounded-full border border-background-50/25 px-3 py-1">
                    {topicMovies.length} 部作品
                  </span>
                  <span className="rounded-full border border-background-50/25 px-3 py-1">含推荐理由</span>
                  <span className="rounded-full border border-background-50/25 px-3 py-1">相关专题 {relatedSpecials.length} 个</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_280px] lg:gap-10">
            <div className="min-w-0">
              <div>
                <p className="flex items-center gap-2 font-label text-xs font-bold uppercase tracking-[0.24em] text-accent-700">
                  <span className="h-px w-6 bg-accent-500" />
                  Why This List
                </p>
                <h2 className="mt-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">推荐理由</h2>
                <div className="mt-4 flex flex-col gap-3">
                  {selected.reasons.map((reason, index) => (
                    <div
                      key={reason}
                      className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 font-label text-xs font-bold text-accent-700">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <p className="text-sm leading-7 text-foreground-700">{reason}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
                    <span className="h-4 w-1 rounded-full bg-accent-500" />
                    本期片单
                  </h2>
                  <p className="text-xs text-foreground-500">点击卡片查看完整剧情解析</p>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 xl:grid-cols-3">
                  {topicMovies.map((movie, index) => (
                    <MovieCard key={movie.id} movie={movie} index={index} />
                  ))}
                </div>
                {topicMovies.length === 0 && (
                  <div className="mt-5 rounded-lg border border-dashed border-background-300 px-6 py-12 text-center">
                    <p className="text-sm text-foreground-500">该专题片单正在整理中，请稍后再来。</p>
                  </div>
                )}
              </div>
            </div>

            <aside className="w-full">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5 lg:sticky lg:top-28">
                <h2 className="font-heading text-sm font-bold text-foreground-950">相关专题</h2>
                <div className="mt-3 flex flex-col gap-2">
                  {relatedSpecials.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSearchParams({ topic: item.id }, { replace: true })}
                      className="group flex items-start gap-2 rounded-md bg-background-50 px-3 py-3 text-left transition-colors hover:bg-secondary-100"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                        <i className="ri-bookmark-3-line text-sm" aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-xs font-semibold text-foreground-800 transition-colors group-hover:text-primary-700 line-clamp-2">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-400">{item.movieIds.length} 部作品</span>
                      </span>
                    </button>
                  ))}
                  {relatedSpecials.length === 0 && (
                    <p className="text-xs leading-6 text-foreground-500">暂无关联专题，可以在上方切换其他主题。</p>
                  )}
                </div>

                <div className="mt-5 border-t border-background-200 pt-4">
                  <h3 className="text-xs font-semibold text-foreground-700">继续探索</h3>
                  <div className="mt-2 flex flex-col gap-2">
                    <Link
                      to="/category"
                      className="flex items-center justify-between rounded-md px-3 py-2 text-xs text-foreground-600 transition-colors hover:bg-background-50 hover:text-primary-700"
                    >
                      全部影视剧分类
                      <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                    </Link>
                    <Link
                      to="/news"
                      className="flex items-center justify-between rounded-md px-3 py-2 text-xs text-foreground-600 transition-colors hover:bg-background-50 hover:text-primary-700"
                    >
                      最新影视资讯
                      <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                    </Link>
                    <Link
                      to="/search"
                      className="flex items-center justify-between rounded-md px-3 py-2 text-xs text-foreground-600 transition-colors hover:bg-background-50 hover:text-primary-700"
                    >
                      全站搜索
                      <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}